import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FeatureCollection, Point } from 'geojson';
import type { GeolocateControl as MapboxGeolocateControl } from 'mapbox-gl';
import Map, {
  MapRef,
  Source,
  Layer,
  GeolocateControl,
  AttributionControl,
  Marker,
  GeolocateResultEvent,
} from 'react-map-gl';

import CourseDetailDrawer from '../../components/drawers/CourseDetailDrawer';
import CourseRequestDrawer from '../../components/drawers/CourseRequestDrawer';
import MemberSetupDrawer from '../../components/drawers/MemberSetupDrawer';
import UpgradeDrawer from '../../components/drawers/UpgradeDrawer';
import { isSameCourse } from '../../components/maps/CourseCard';
import CourseFilterDrawer, {
  COURSE_FILTER_KINDS,
  CourseFilterKind,
  countActiveFilters,
  EMPTY_GEO_FILTER,
  GeoFilter,
  PlaceOption,
} from '../../components/maps/CourseFilterDrawer';
import CourseListView from '../../components/maps/CourseListView';
import ExploreAccountButton from '../../components/maps/ExploreAccountButton';
import ExploreFilterChips from '../../components/maps/ExploreFilterChips';
import ExploreSearchBar from '../../components/maps/ExploreSearchBar';
import ExploreViewToggle, {
  ExploreView,
} from '../../components/maps/ExploreViewToggle';
import MapStyleDrawer, {
  MapStyleControl,
} from '../../components/maps/MapStyleDrawer';
import NearbyCoursesStrip from '../../components/maps/NearbyCoursesStrip';
import { MAP_BOX_KEY } from '../../config/config';
import {
  clusterLayer,
  clusterCountLayer,
  buildUnclusteredPointLayer,
  buildUnclusteredPointLabelLayer,
  coursePinScaleForZoom,
  COURSE_PIN_IMAGE,
  COURSE_PIN_WIDTH,
  COURSE_PIN_HEIGHT,
} from '../../config/exploreMapLayers';
import {
  ExploreMapStyleId,
  getExploreMapStyle,
  mapStyleUrl,
  readStoredMapStyleId,
  storeMapStyleId,
} from '../../config/exploreMapStyles';
import { CourseImageConstants } from '../../constants/CourseImageConstants';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useExploreCourses } from '../../hooks/useExploreCourses';
import { useMyCourseFlags } from '../../hooks/useMyCourseFlags';
import { CourseMinifiedDTO } from '../../services/apiClient';
import { createCourseSearch } from '../../utils/courseSearch';
import { calculateDistanceMeters } from '../../utils/geoUtils';
import { tapHaptic } from '../../utils/hapticUtils';
import { isSignedIn } from '../../utils/tokenUtils';

import 'mapbox-gl/dist/mapbox-gl.css';

// No geolocation permission granted (or unavailable) — a public discovery map should never
// hard-block on that, so fall back to a neutral world view rather than one specific country.
// The strip shows a handful, the list view pages through the rest. Both read the same sorted
// array, so this is a slice size, not a second query.
const STRIP_COURSE_COUNT = 25;

// Whether the screen is narrow enough for the nearby strip, which is `sm:hidden` (Tailwind's
// default sm, 640px). Read at tap time rather than tracked in state: a pin tap only needs to know
// the width at that moment.
const isStripWidth = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 639.98px)').matches;

// Hard ceiling on the sorted "nearest" array. The sort itself runs over the whole set either way,
// but *retaining* all 41k would hold a second full copy of the dataset alongside the one the map
// already has. Nobody scrolls a nearest-to-me list past 500; browsing the entire set is a
// different feature (filter/search), not this one.
const MAX_NEARBY_COURSES = 500;

// One collator for the A to Z list rather than localeCompare per pair: a big country is thousands
// of compares, and localeCompare builds its rules afresh each call. Base sensitivity, so case and
// accents do not split "Élan" from "Elan".
const NAME_COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base' });

// How far one click on a cluster is allowed to zoom in. `getClusterExpansionZoom` returns the
// zoom at which the cluster finally breaks apart, which from a world view is a jump of six or
// seven levels — the map lands somewhere unrecognisable.
//
// The step shrinks as the visitor gets closer, because a zoom *level* is not a fixed amount of
// travel: each one halves the ground in frame. Two levels off the globe is a helpful stride; two
// levels while looking at a town throws away three quarters of what is on screen. Same number,
// very different move — which is why one flat step felt right far out and far too sensitive in.
const clusterZoomStep = (zoom: number): number => {
  if (zoom < 6) {
    // Globe to continent. Nothing here is recognisable yet, so cover ground.
    return 2;
  }
  if (zoom < 11) {
    // Country to region.
    return 1.25;
  }
  // Town and closer. Clusters this far in are a few courses sitting almost on top of each other,
  // and the surroundings are the useful part of the view — creep in.
  return 0.75;
};

// How close the map is allowed to get when it flies to the visitor's location. A regional view:
// close enough to see where you are and what is around you, far enough that the surrounding
// courses stay on screen. Deliberately not the accuracy circle's own fit, which on a good GPS
// fix is a rooftop.
const LOCATE_MAX_ZOOM = 9;

// How much of the reported accuracy radius the "you are here" circle actually draws. Mapbox
// sizes that circle straight from coords.accuracy, and a desktop fix resolves via the ISP rather
// than a GPS radio — here that is a ~12km radius (see the padding note in handleGeolocate), which
// paints a disc wide enough to swallow the whole region and reads as noise rather than as
// information. A fifth of it still marks the area without taking over the map.
//
// Note this makes the circle *indicative*, not a true accuracy radius: it now under-states how
// coarse the fix really is. The camera framing below is unaffected and still pads off the real
// reported value.
const ACCURACY_CIRCLE_SCALE = 0.2;

// The private surface of GeolocateControl that sizes the accuracy circle. `_accuracy` is the sole
// input to `_updateCircleRadius`, and the control recomputes from it on every zoom, so writing it
// once per fix keeps the smaller circle correct at every zoom level.
//
// Reached into deliberately: the control offers nothing between drawing the circle at the full
// reported accuracy and `showAccuracyCircle={false}`, which removes it altogether. Kept in one
// typed shim so a mapbox-gl upgrade has a single place to fix, and so the guard below degrades to
// the stock circle instead of throwing if these are ever renamed.
type AccuracyCircleInternals = {
  _accuracy?: number;
  _updateCircleRadius?: () => void;
};

const shrinkAccuracyCircle = (control: MapboxGeolocateControl | null): void => {
  const internals = control as unknown as AccuracyCircleInternals | null;
  if (
    !internals ||
    typeof internals._accuracy !== 'number' ||
    typeof internals._updateCircleRadius !== 'function'
  ) {
    return;
  }
  internals._accuracy *= ACCURACY_CIRCLE_SCALE;
  internals._updateCircleRadius();
};

// How much ground to frame around a course when one is picked. 20km puts the club in the middle
// of its own neighbourhood: near enough to read the streets around it, wide enough that the other
// clubs worth knowing about are still on screen. Picking a course used to leave the zoom exactly
// where the visitor had it, which meant selecting from a world view left them staring at a pin on
// a continent.
const COURSE_FRAME_RADIUS_METRES = 20000;

// A square of `metres` in every direction around a point, as the [[w, s], [e, n]] pair fitBounds
// wants. 111320 is metres per degree of latitude; longitude degrees shrink towards the poles by
// cos(lat), and the 0.01 floor keeps that from dividing by ~zero at the poles themselves.
const boundsAround = (
  latitude: number,
  longitude: number,
  metres: number,
): [[number, number], [number, number]] => {
  const dLat = metres / 111320;
  const dLon =
    metres / (111320 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.01));

  return [
    [longitude - dLon, latitude - dLat],
    [longitude + dLon, latitude + dLat],
  ];
};

// How much has to be typed before the search runs. Named because two places read it: the results
// themselves, and the "no matches" line, which must not appear while the box is still too short
// to have searched anything.
const SEARCH_MIN_CHARS = 2;

// The map's own zoom limits, named so the camera maths and the <Map> props cannot drift apart.
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 17;

// How close the camera may get when it frames a whole country or region. A place with one course
// in it would otherwise fit to a single point and dive to street level, which answers "where is
// this place" with a rooftop.
const PLACE_FRAME_MAX_ZOOM = 9;

// How much of a place's course set the frame is allowed to ignore at each edge, and the smallest
// set that trimming is applied to at all.
//
// Trimming exists because a single bad coordinate ruins the frame for everyone. Australia's set
// framed on its true min and max landed the camera near Indonesia at world zoom: some rows sit
// thousands of kilometres from the country they name, which is the same legacy free-text
// latitude/longitude problem the migrator already clamps for (see CLAUDE.md). Cutting 5% off each
// edge frames where the country's courses actually are and lets a handful of strays fall outside
// the view, which is the right trade for a camera move.
//
// Below the threshold every course counts: a province with eight clubs has no outliers to speak
// of, and dropping one of eight would be throwing away real data.
const PLACE_FRAME_TRIM = 0.05;
const PLACE_FRAME_TRIM_MIN_COURSES = 20;

// The value at a position through a sorted list, e.g. 0.05 for the 5th percentile.
const quantile = (sorted: number[], fraction: number): number =>
  sorted[
    Math.min(
      sorted.length - 1,
      Math.max(0, Math.round((sorted.length - 1) * fraction)),
    )
  ];

// A box around the courses given, as fitBounds wants it, or null when none of them has a
// location.
//
// Longitude is worked out twice. A set that straddles the antimeridian (Fiji sits on it, and the
// United States reaches from Hawaii to Maine) has a naive west-to-east span of nearly the whole
// globe, so fitting it zooms out to the world and frames nothing. Measuring again with the
// negative longitudes shifted past 180 gives the real span for such a set, and the smaller of the
// two is the one that describes it. Mapbox accepts a longitude above 180 and wraps it itself.
const boundsForCourses = (
  courses: CourseMinifiedDTO[],
): [[number, number], [number, number]] | null => {
  const located = courses.filter(
    (c) => c.latitude != null && c.longitude != null,
  );
  if (located.length === 0) {
    return null;
  }

  const lngs = located.map((c) => c.longitude as number);
  const shifted = lngs.map((lng) => (lng < 0 ? lng + 360 : lng));

  const span = (values: number[]): number =>
    Math.max(...values) - Math.min(...values);
  const useShifted = span(shifted) < span(lngs);

  const sortedLngs = [...(useShifted ? shifted : lngs)].sort((a, b) => a - b);
  const sortedLats = located
    .map((c) => c.latitude as number)
    .sort((a, b) => a - b);

  const trim =
    located.length >= PLACE_FRAME_TRIM_MIN_COURSES ? PLACE_FRAME_TRIM : 0;

  return [
    [quantile(sortedLngs, trim), quantile(sortedLats, trim)],
    [quantile(sortedLngs, 1 - trim), quantile(sortedLats, 1 - trim)],
  ];
};

// The detail drawer's width in CSS pixels (w-96 = 24rem at a 16px root). The drawer is opaque and
// covers the right-hand edge of the map, so every camera move that frames a course has to keep the
// club clear of it. Below `sm` the drawer is hidden and there is nothing to avoid.
const DRAWER_WIDTH_PX = 384;

const drawerPaddingRight = (): number =>
  window.innerWidth >= 640 ? DRAWER_WIDTH_PX : 0;

// The camera `fitBounds` would settle on for these bounds — the same min(scaleX, scaleY) Mapbox
// computes internally, in normalised Mercator units where the whole world is 1.0 across and a tile
// is 512px.
//
// This exists because handleViewChange cannot call fitBounds. The <Map> is unmounted while the
// list view is up, and an imperative camera move made as it mounts is undone by the very next
// render — the map is a *controlled* component, so viewState always wins. Computing the camera and
// putting it into viewState is the same move, expressed as state instead of a method call.
//
// The longitude comes back shifted east by half the padding: fitBounds keeps its centre on the
// bounds and offsets the *rendering* through the transform's own padding, which state cannot set,
// so the shift has to be baked into the centre instead. Without it the club lands at the centre of
// the canvas rather than the centre of the part of it you can actually see.
const cameraForBounds = (
  [[west, south], [east, north]]: [[number, number], [number, number]],
  maxZoom: number = MAX_ZOOM,
): { longitude: number; latitude: number; zoom: number } => {
  const latitude = (south + north) / 2;
  const longitude = (west + east) / 2;

  const mercatorY = (lat: number): number => {
    const s = Math.sin((lat * Math.PI) / 180);
    return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
  };

  const paddingRight = drawerPaddingRight();
  const usableWidth = Math.max(window.innerWidth - paddingRight, 1);

  const zoom = Math.min(
    Math.max(
      Math.min(
        Math.log2(usableWidth / (512 * ((east - west) / 360))),
        Math.log2(
          window.innerHeight / (512 * (mercatorY(south) - mercatorY(north))),
        ),
      ),
      MIN_ZOOM,
    ),
    maxZoom,
  );

  const degreesPerPixel = 360 / (512 * Math.pow(2, zoom));

  return {
    latitude,
    longitude: longitude + (paddingRight / 2) * degreesPerPixel,
    zoom,
  };
};

// The camera for a single club's 20km frame. A thin wrapper now that the maths above takes any
// bounds, kept because the call sites read better for naming what they are framing.
const cameraForCourseFrame = (
  latitude: number,
  longitude: number,
): { longitude: number; latitude: number; zoom: number } =>
  cameraForBounds(
    boundsAround(latitude, longitude, COURSE_FRAME_RADIUS_METRES),
  );

const DEFAULT_VIEW = {
  latitude: 20,
  longitude: 0,
  zoom: 1.5,
  bearing: 0,
  pitch: 0,
};

// Registers the course pin PNG with the Mapbox style. The `unclustered-point` layer names this
// image but cannot supply it — a symbol layer whose `icon-image` is missing draws *nothing*, with
// no fallback marker and nothing on screen to say why, so getting this wrong looks exactly like
// "the courses stopped loading".
//
// Guarded and idempotent because it is called from two places, and both are needed: `onLoad`
// covers the normal path, and `styleimagemissing` covers the race where the layer mounts before
// the PNG has finished downloading, plus any later style reload that would drop the image again.
const ensureCoursePinImage = (map: mapboxgl.Map): void => {
  if (map.hasImage(COURSE_PIN_IMAGE)) {
    return;
  }
  map.loadImage(CourseImageConstants.COURSE_PIN, (error, image) => {
    // hasImage again: the two callers can both be in flight at once, and addImage throws on a
    // duplicate name rather than ignoring it.
    if (error || !image || map.hasImage(COURSE_PIN_IMAGE)) {
      return;
    }
    map.addImage(COURSE_PIN_IMAGE, image);
  });
};

const Explore: React.FC = () => {
  const { courses, isLoading } = useExploreCourses();
  const mapRef = useRef<MapRef | null>(null);
  const geolocateRef = useRef<MapboxGeolocateControl | null>(null);
  const hasFramedLocationRef = useRef(false);
  // Separate from hasFramedLocationRef on purpose. That one guards the *camera*; this one guards
  // the permission/trigger call itself, which must fire once per visit rather than once per map
  // mount — see handleMapLoad.
  const hasTriggeredLocateRef = useRef(false);

  const [viewState, setViewState] = useState(DEFAULT_VIEW);
  // Map vs full-page list. Everything the two views share — selection, origin, the visitor's
  // location, the search term — is held here, above both, so switching view cannot lose it.
  const [view, setView] = useState<ExploreView>('map');
  // Which ground the map draws. Seeded from the visitor's last choice rather than defaulted, so
  // someone who picked Satellite yesterday does not land on Monochrome today and pick it again.
  // The lazy initialiser matters: reading localStorage on every render is wasted work, and this
  // one is read once per mount.
  const [mapStyleId, setMapStyleId] =
    useState<ExploreMapStyleId>(readStoredMapStyleId);
  const mapStyle = getExploreMapStyle(mapStyleId);
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const [selectedCourse, setSelectedCourse] =
    useState<CourseMinifiedDTO | null>(null);
  // A search-bar pick or a map-pin click changes what the "nearby" strip is sorted against.
  // A strip card click does not — that would reshuffle the strip out from under the card the
  // visitor just tapped.
  const [originCourse, setOriginCourse] = useState<CourseMinifiedDTO | null>(
    null,
  );

  // Geolocation is driven through the GeolocateControl rather than a bare
  // navigator.geolocation call in a mount effect. The effect version raced the map: it ran on
  // first render, when mapRef could still be null, and silently fell back to a hard view jump
  // instead of the fly-in. It also left the control showing "inactive" with no user dot, since
  // the control had no idea we'd located anyone.
  //
  // Triggering on the map's load event means the map is guaranteed ready, so the descent from
  // the world view always plays. No user gesture is needed for the permission prompt — it just
  // has to be a secure context (localhost counts).
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      ensureCoursePinImage(map);
      // Safety net for the race and for any future style swap — see ensureCoursePinImage.
      map.on('styleimagemissing', () => ensureCoursePinImage(map));
    }

    // Once per visit, not once per mount. Switching to the list view unmounts the <Map>, so this
    // runs again on the way back — on a *fresh* GeolocateControl, which then flies the camera to
    // the visitor and throws away the course framing handleViewChange just applied. The visitor
    // picks a club in the list, switches to the map, and lands on themselves instead.
    // hasFramedLocationRef does not cover this: it guards our own fitBounds in handleGeolocate,
    // not the control's internal _updateCamera, which runs first and is not ours to skip.
    if (hasTriggeredLocateRef.current) {
      return;
    }
    hasTriggeredLocateRef.current = true;
    geolocateRef.current?.trigger();
  }, []);

  // Fired on every fix while trackUserLocation is on, so this keeps the "nearby" strip's origin
  // following the user rather than pinned to wherever they first loaded the page.
  const handleGeolocate = useCallback((event: GeolocateResultEvent) => {
    const { latitude, longitude, accuracy } = event.coords;
    setUserLocation({ latitude, longitude });

    // Before the first-fix return below, not after: the control rewrites `_accuracy` from the raw
    // reading on every fix, so the circle has to be shrunk again each time or it springs back to
    // full size on the second fix.
    shrinkAccuracyCircle(geolocateRef.current);

    // Only reframe on the first fix. Later fixes still update the origin above, but must not
    // yank the camera back while the user is panning around.
    if (hasFramedLocationRef.current) {
      return;
    }
    hasFramedLocationRef.current = true;

    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }

    // Locating the visitor *does* fly them in — this is the one automatic camera move that is
    // wanted, because a dot on a globe at zoom 1.5 is a few pixels and tells you nothing. What it
    // must not do is land on a rooftop: LOCATE_MAX_ZOOM caps it at a regional view, so the
    // surrounding courses stay in frame. That cap is the whole difference between this and the
    // behaviour that was asked to be taken out.
    //
    // Mapbox's own _updateCamera does fitBounds(center.toBounds(accuracy)) — the accuracy circle
    // and nothing else — then fires this event, so we get the last word on the camera.
    //
    // That unpadded fit is wrong for a coarse fix. Desktop geolocation here resolves via the ISP
    // rather than the device (no GPS radio), which in testing landed ~36km out with a ~12km
    // accuracy radius. Fitting just that circle on a narrow viewport leaves the user's real
    // position off-screen entirely. Padding to a few times the radius keeps the surrounding
    // region — and whatever courses are actually near them — in frame.
    // 4x, not 3x: the observed real-world error was ~36km against a ~12km reported accuracy, so
    // the true position sat at 3x the radius. Padding 3x would put it exactly on the frame edge.
    // The 2km floor stops a good GPS fix (accuracy in metres) from framing an absurdly tight box;
    // LOCATE_MAX_ZOOM is what actually caps the close-in case.
    const padMetres = Math.min(Math.max(accuracy * 4, 2000), 150000);

    map.fitBounds(
      boundsAround(latitude, longitude, padMetres),
      { maxZoom: LOCATE_MAX_ZOOM, duration: 2200 },
      // Tags this as a geolocate-driven move. Without it the control sees an external camera
      // change and drops ACTIVE_LOCK to BACKGROUND, so the dot stops following the user.
      { geolocateSource: true },
    );
  }, []);

  // Played and bucket-list are read here, not in the drawer, because the drawer is unmounted-ish
  // (it stays mounted but its course changes on every pin click) and because the list card and the
  // strip read the same two sets. Home courses come along read-only, for the filter bar.
  //
  // The hook owns the saving: it loads the member's rows on mount and writes each tick straight to
  // the member-scoped API, optimistically. Ids inside the sets are lower-cased, per CLAUDE.md:
  // a pin click hands the id back through Mapbox's serialized feature properties rather than from
  // the original object, and casing is not guaranteed to survive that round trip.
  //
  // Read this high up the component on purpose. The filter below narrows the course set that the
  // map source, the search index and the nearby ordering are all built from, so the sets have to
  // exist before any of them.
  // The end of the free tier, held here rather than inside the hook: the hook's job is the saved
  // state, and this is a piece of screen. The message is the backend's own, shown as-is.
  const [freeLimitMessage, setFreeLimitMessage] = useState<string | null>(null);

  const handleFreeLimitReached = useCallback((message: string): void => {
    setFreeLimitMessage(message);
  }, []);

  const {
    playedCourseIds,
    bucketListCourseIds,
    homeCourseIds,
    setPlayed,
    setBucketListed,
    refresh: refreshMyCourses,
  } = useMyCourseFlags({ onFreeLimitReached: handleFreeLimitReached });

  const isCourseIn = (ids: Set<string>, id: string | undefined): boolean =>
    id != null && ids.has(id.toLowerCase());

  // Which of the member's own lists the map is narrowed to. Empty is the default and means no
  // filter at all, so a first visit and a signed-out visitor both get the whole 41k.
  //
  // ?list=played (or home, bucket) starts it on one list: the profile's gold countries map opens
  // the member's played courses this way. Only for a signed-in member, since the lists are theirs.
  // The effect beside the ?reg=1 one below wipes it from the address bar.
  const [courseFilters, setCourseFilters] = useState<Set<CourseFilterKind>>(
    () => {
      const list = new URLSearchParams(window.location.search).get('list');
      return isSignedIn() &&
        COURSE_FILTER_KINDS.includes(list as CourseFilterKind)
        ? new Set([list as CourseFilterKind])
        : new Set();
    },
  );

  const toggleCourseFilter = (kind: CourseFilterKind): void => {
    // A new Set each time: mutating and returning the same one is the same reference, so React
    // skips the render and the chip never lights up. Same reason as withId in useMyCourseFlags.
    setCourseFilters((current) => {
      const next = new Set(current);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  };

  const filterCounts = useMemo(
    () => ({
      home: homeCourseIds.size,
      played: playedCourseIds.size,
      bucket: bucketListCourseIds.size,
    }),
    [homeCourseIds, playedCourseIds, bucketListCourseIds],
  );

  // A member with three empty lists gets three dead chips, which is chrome that does nothing.
  // The bar appears the moment they tick their first course.
  const hasAnyCourseFlags =
    filterCounts.home + filterCounts.played + filterCounts.bucket > 0;

  // Country, then region inside it. Held beside the chips rather than inside them because it is
  // a different kind of question: the chips ask which of your lists, this asks where.
  const [geoFilter, setGeoFilter] = useState<GeoFilter>(EMPTY_GEO_FILTER);

  // Every country the course set actually has, and the regions inside the chosen one. Read off
  // the courses rather than fetched: the rows carry countryName and stateName, so a lookup call
  // would be a second, slower copy of what is already on the page. One pass over 41k strings,
  // memoised, and `courses` only changes when the whole set reloads.
  // Each place carries how many courses sit behind it, so the picker can say whether a country is
  // worth opening before it is opened. Counted in the same pass that collects the names: a second
  // pass over 41k rows for a number we already have in hand would be waste.
  // A plain record rather than a Map, because `Map` in this file is react-map-gl's component:
  // the import shadows the global, so `new Map()` here is a type error, not a dictionary.
  const toSortedOptions = (counts: Record<string, number>): PlaceOption[] =>
    Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));

  const filterCountries = useMemo(() => {
    const counts: Record<string, number> = {};
    courses.forEach((c) => {
      if (c.countryName) {
        counts[c.countryName] = (counts[c.countryName] ?? 0) + 1;
      }
    });
    return toSortedOptions(counts);
  }, [courses]);

  const filterRegions = useMemo(() => {
    if (geoFilter.country == null) {
      return [];
    }
    const counts: Record<string, number> = {};
    courses.forEach((c) => {
      if (c.countryName === geoFilter.country && c.stateName) {
        counts[c.stateName] = (counts[c.stateName] ?? 0) + 1;
      }
    });
    return toSortedOptions(counts);
  }, [courses, geoFilter.country]);

  // The one place every filter is applied. Everything downstream (the map source, the Fuse index,
  // the nearby ordering that feeds both the strip and the list view) is built from this rather
  // than from `courses`, so the three surfaces cannot disagree about what is on the map.
  //
  // The two filters are an "and": the chips narrow to your own lists, the place narrows to where.
  // Played plus Scotland is the courses you have played in Scotland, which is the only useful
  // reading of setting both. Within the chips themselves it is still a union, so Played plus
  // Bucket list is both lists rather than the overlap.
  //
  // Place is applied before the nearest-500 cap downstream, not after, so picking a country
  // really does mean every course in it and not just the ones near you.
  const visibleCourses = useMemo(() => {
    let result = courses;

    if (courseFilters.size > 0) {
      const wanted = new Set<string>();
      if (courseFilters.has('home')) {
        homeCourseIds.forEach((id) => wanted.add(id));
      }
      if (courseFilters.has('played')) {
        playedCourseIds.forEach((id) => wanted.add(id));
      }
      if (courseFilters.has('bucket')) {
        bucketListCourseIds.forEach((id) => wanted.add(id));
      }

      result = result.filter(
        (c) => c.id != null && wanted.has(c.id.toLowerCase()),
      );
    }

    if (geoFilter.country != null) {
      result = result.filter((c) => c.countryName === geoFilter.country);
    }

    if (geoFilter.region != null) {
      result = result.filter((c) => c.stateName === geoFilter.region);
    }

    return result;
  }, [
    courses,
    courseFilters,
    homeCourseIds,
    playedCourseIds,
    bucketListCourseIds,
    geoFilter.country,
    geoFilter.region,
  ]);

  const geoJsonData: FeatureCollection<Point, CourseMinifiedDTO> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: visibleCourses
        .filter((c) => c.longitude != null && c.latitude != null)
        .map((c) => ({
          type: 'Feature',
          properties: c,
          geometry: {
            type: 'Point',
            coordinates: [c.longitude as number, c.latitude as number],
          },
        })),
    }),
    [visibleCourses],
  );

  // Rebuilt when the filter changes, which is the point: with Played switched on, searching must
  // not offer a course that is no longer on the map. Exact name matches first, fuzzy only as a
  // typo fallback: see utils/courseSearch.ts for why plain fuzzy search was wrong.
  const courseSearch = useMemo(
    () => createCourseSearch(visibleCourses),
    [visibleCourses],
  );

  const searchResults = useMemo(() => {
    if (debouncedSearchTerm.trim().length < SEARCH_MIN_CHARS) {
      return [];
    }
    return courseSearch(debouncedSearchTerm, 8);
  }, [courseSearch, debouncedSearchTerm]);

  // Picking a country or a region takes the camera there. Without this the filter thinned the
  // pins out but left the visitor looking at wherever they already were, so choosing Australia
  // from a view of Europe emptied the screen rather than showing Australia.
  //
  // It frames the filtered courses rather than a country centroid, which means no lookup table of
  // places and no new data: every course already carries its own coordinates, and the box around
  // them is exactly what the filter put on the map. A country whose courses sit in one corner of
  // it is framed on that corner, which is the more useful answer anyway.
  //
  // Keyed on the place alone. The chips are deliberately left out: narrowing to your own played
  // courses inside a country should not throw the camera across that country, and clearing the
  // place is not a request to go anywhere either.
  // Through viewState, not map.fitBounds. The <Map> is a controlled component here, so viewState
  // always wins: an imperative move made from an effect is undone by the render that follows it.
  // That was the first version of this and the camera never budged. cameraForBounds computes what
  // fitBounds would have settled on, including the offset that keeps the country clear of the
  // open filter drawer, and setting it as state is the same move expressed the way this map
  // actually takes instructions. handleViewChange does the same thing for the same reason.
  useEffect(() => {
    if (geoFilter.country == null && geoFilter.region == null) {
      return;
    }

    const bounds = boundsForCourses(visibleCourses);
    if (!bounds) {
      return;
    }

    setViewState((current) => ({
      ...current,
      ...cameraForBounds(bounds, PLACE_FRAME_MAX_ZOOM),
    }));
    // visibleCourses is deliberately not a dependency: it changes with the chips too, and this
    // effect is about the place. It is read fresh when the place changes, which is the only time
    // this runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoFilter.country, geoFilter.region]);

  // Camera only. Selection deliberately does NOT happen here: in list view there is no map, so
  // this bails out early, and a selection folded into it would be silently dropped.
  const flyToCourse = (course: CourseMinifiedDTO): void => {
    const map = mapRef.current;
    if (!map || course.longitude == null || course.latitude == null) {
      return;
    }
    // Frame COURSE_FRAME_RADIUS_METRES around the club rather than naming a zoom level, because a
    // zoom level is not a fixed amount of ground — the same number covers far less at the equator
    // than near the poles. fitBounds on a square of known size lands on the same *view* wherever
    // the club is.
    //
    // This deliberately replaces the old recenter-only behaviour. It is still not the thing that
    // was taken out before that, which forced `Math.max(getZoom(), 14)` and slammed the camera to
    // street level: 20km keeps the surrounding clubs in frame, which is the whole point.
    //
    // Padded on the right by the drawer's own width. The drawer is opaque and covers 24rem of the
    // canvas, so an unpadded fit spends nearly a third of the framed neighbourhood behind it —
    // which defeats the one thing the 20km frame is for. Only from `sm` up: below that the drawer
    // is hidden and the strip sits at the bottom instead.
    map.fitBounds(
      boundsAround(
        course.latitude,
        course.longitude,
        COURSE_FRAME_RADIUS_METRES,
      ),
      {
        duration: 1200,
        padding: { top: 0, bottom: 0, left: 0, right: drawerPaddingRight() },
      },
    );
  };

  // Opening a course. Works in either view — the camera move is the optional half, so a card
  // tapped in the list still selects, and is still selected after switching to the map.
  // Selecting a course centres the map on it and marks it with the bouncing pin. It does *not*
  // open the detail drawer: the phone strip is always on screen and that drawer covers the map
  // full-width there, so a card tap would bury what the visitor is browsing. The strip's View
  // button is what opens it — see handleViewCourse.
  const handleCourseSelect = (course: CourseMinifiedDTO): void => {
    setSelectedCourse(course);
    flyToCourse(course);
  };

  // Select *and* show the detail. Every entry point except a strip card tap goes through here: a
  // pin click, a search pick, a list card, the drawer's own next-nearby row, and the strip's View
  // button. Kept as one state rather than "the drawer is open when a course is selected", which is
  // what it used to be, because those are now genuinely different questions.
  const handleViewCourse = (course: CourseMinifiedDTO): void => {
    handleCourseSelect(course);
    setIsDetailOpen(true);
  };

  // Picking a course — from the search bar, or by clicking its pin on the map — opens it and
  // re-centers the "nearby" ordering on it, so both views read as "nearest to this course".
  // Card taps in the strip and the list deliberately do not: re-sorting would move the card that
  // was just tapped out from under the finger.
  const handleCourseFocus = (course: CourseMinifiedDTO): void => {
    handleViewCourse(course);
    setOriginCourse(course);
  };

  const handleSearchSelect = (course: CourseMinifiedDTO): void => {
    handleCourseFocus(course);
    setSearchTerm('');
  };

  // D7: browsing is open to everyone, the first *write* is the gate. Held here rather than in
  // either drawer because all three surfaces that can trigger it — the detail drawer's checkboxes,
  // the list card and the nearby strip — already route through this component's handlers.
  // Whether the course detail drawer is out. See handleCourseSelect for why this is not simply
  // "a course is selected" any more.
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [memberSetupIntent, setMemberSetupIntent] = useState<string | null>(
    null,
  );
  const [isMemberSetupOpen, setIsMemberSetupOpen] = useState(false);

  // Which tab the gate opens on. Everything on this page wants sign in (D7: they already have an
  // account, they just got stopped mid-tick). The public site is the exception, see below.
  const [memberSetupMode, setMemberSetupMode] = useState<'signIn' | 'signUp'>(
    'signIn',
  );

  // Bumped whenever the session changes, so the account menu re-reads the token. Deliberately not
  // a copy of "is signed in": requireMember below still calls isSignedIn() fresh every time,
  // because a token can expire without anything here re-rendering.
  const [sessionVersion, setSessionVersion] = useState(0);

  // Whether to show the filter bar at all. Read fresh on every render rather than memoised or
  // held in state, exactly as requireMember does: signing in or out on this page never remounts
  // anything, and sessionVersion above already forces the render. It is a decision about chrome,
  // not a permission check, so a stale read costs nothing: the sets the bar filters by come back
  // empty and it hides itself on the next render.
  const isMember = isSignedIn();

  // Whether to offer the member's own three lists inside the filter menu. A signed-out visitor
  // has none of them, so every chip would be an empty map; a member with all three empty gets
  // three dead chips, which is chrome that does nothing. Both cases leave the place filter, which
  // works the same either way.
  const showKindFilters = isMember && hasAnyCourseFlags;

  // How tall the nearby strip is right now, reported by the strip itself. Feeds the CSS variable
  // on the page root that index.css uses to park the map's locate button just above it. 0 means
  // no strip, which is also what the strip reports on its way out.
  const [stripHeight, setStripHeight] = useState(0);
  const handleStripHeightChange = useCallback((height: number): void => {
    setStripHeight(height);
  }, []);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMapStyleOpen, setIsMapStyleOpen] = useState(false);
  const [isCourseRequestOpen, setIsCourseRequestOpen] = useState(false);
  const [courseRequestName, setCourseRequestName] = useState('');

  const activeFilterCount = countActiveFilters(courseFilters, geoFilter);

  // What the courses are narrowed to right now, in the words the member picked, for the search
  // box to name when it finds nothing. The most specific one wins: a region is inside a country,
  // so saying "no matches in Free State" is more use than naming South Africa. The chips have no
  // place name of their own, hence the catch-all.
  const filterScope =
    geoFilter.region ??
    geoFilter.country ??
    (courseFilters.size > 0 ? 'your filtered courses' : null);

  const clearAllFilters = (): void => {
    setCourseFilters(new Set());
    setGeoFilter(EMPTY_GEO_FILTER);
  };

  // The one place the gate is applied. Signed in, the action runs; signed out, the member-setup
  // drawer comes up and the action does *not* run and is not queued — D10 is explicit that the
  // visitor ticks again after logging in rather than having it replayed for them.
  //
  // isSignedIn reads the token on every call instead of a value captured at render: a visitor can
  // log in, or a token can expire, while this page is open and this component never re-renders for
  // either.
  const requireMember = (intent: string, action: () => void): void => {
    if (isSignedIn()) {
      action();
      return;
    }
    setMemberSetupIntent(intent);
    setIsMemberSetupOpen(true);
  };

  // Charcoal ground under this page while it is open. iOS 15 and later paint a home-screen app's
  // status bar with the page's background colour, so the meta status-bar-style tag is not enough
  // on its own: with no background the strip above the page came out white. Since the list view
  // started scrolling the page itself, that strip and any overscroll show this colour, so it is
  // the list's charcoal. On <html> as well as <body> so it is the page's own background, not one
  // that only reaches the canvas by propagating up from body. Scoped to this page (and cleaned up
  // on unmount) because only Explore is dark; the forms and the admin shell are light by design.
  useEffect(() => {
    const targets = [document.documentElement, document.body];
    targets.forEach((el) => el.classList.add('gp-dark-ground'));
    return (): void => {
      targets.forEach((el) => el.classList.remove('gp-dark-ground'));
    };
  }, []);

  // The public marketing site's "Become a member" button lands here with ?reg=1 rather than on a
  // /register route, because sign-up lives in this drawer and nowhere else. A visitor already
  // signed in is left alone: they are a member, so the map is exactly what they asked for.
  //
  // The flag is then wiped from the address bar with replaceState, so a refresh, a bookmark or a
  // shared link is just the map. No navigation, so nothing else on the page is disturbed.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('reg')) {
      return;
    }

    if (!isSignedIn()) {
      setMemberSetupIntent(null);
      setMemberSetupMode('signUp');
      setIsMemberSetupOpen(true);
    }

    params.delete('reg');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
  }, []);

  // ?list= has been read into courseFilters already; take it out of the address bar the same way,
  // so a refresh or a shared link is just the map.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('list')) {
      return;
    }
    params.delete('list');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
  }, []);

  // Past the gate, a tick is saved. The hook moves the checkbox first and writes to
  // /member/me/bucket-list/{courseId}, putting it back if the save fails. The target state is
  // computed here rather than inside the hook so a double click cannot land on the wrong end.
  const handleBucketList = (course: CourseMinifiedDTO): void => {
    const next = !isCourseIn(bucketListCourseIds, course.id);
    requireMember('to your bucket list', () => {
      // Android only, same rules as handleTogglePlayed below: inside the click, off the target
      // state rather than the saved one, and never for a signed-out visitor.
      tapHaptic(next ? 'added' : 'removed');
      setBucketListed(course.id, next);
    });
  };

  // Still passed by the list card and the strip, and still unrendered — Request to Play is hidden
  // until after launch (D3 in docs/EXPLORE.md), which is a CourseActions decision, not a
  // reason to unpick the wiring here.
  const handleRequestToPlay = (course: CourseMinifiedDTO): void => {
    // eslint-disable-next-line no-console
    console.log('TODO: request to play', course.id, course.name);
  };

  // Asking for a course that is not on GolfPlayed. Behind the same member gate as a tick, so we know
  // who to tell when it is added, and so the queue is not open to anyone with a browser. The search
  // term is carried over as the course name, captured now because the box may be cleared later.
  const handleRequestCourse = (): void => {
    requireMember('your course request', () => {
      setCourseRequestName(searchTerm.trim());
      setIsCourseRequestOpen(true);
    });
  };

  const handleTogglePlayed = (course: CourseMinifiedDTO): void => {
    const next = !isCourseIn(playedCourseIds, course.id);
    requireMember('the courses you have played', () => {
      // Phones only, and it does nothing anywhere else. Fired here rather than off the saved
      // state because it has to run inside the click for the browser to allow it, and because a
      // failed save rolls that state back, which is not something to buzz about.
      // Not fired for a signed-out visitor: that tap opens the sign-up drawer instead of saving
      // anything, so a "saved it" buzz would be a lie.
      tapHaptic(next ? 'added' : 'removed');
      setPlayed(course.id, next);
    });
  };

  // Predicates rather than the Sets themselves, so the card surfaces never learn how the state is
  // stored — and so the lower-casing that Set membership depends on stays in one place.
  const isCoursePlayed = (course: CourseMinifiedDTO): boolean =>
    isCourseIn(playedCourseIds, course.id);

  const isCourseBucketListed = (course: CourseMinifiedDTO): boolean =>
    isCourseIn(bucketListCourseIds, course.id);

  const handleMapStyleChange = (next: ExploreMapStyleId): void => {
    setMapStyleId(next);
    storeMapStyleId(next);
  };

  const handleViewChange = (next: ExploreView): void => {
    // The button that opens it is map-only, so a sheet left up on the way to the list would be
    // stranded there with nothing in the bar to shut it again.
    setIsMapStyleOpen(false);

    // Coming back to the map, the camera is wherever it was left — which is not where a course
    // picked in the list view lives. Without this, the detail panel names a course that is
    // off-screen: pick one 48km up the coast in the list, switch to the map, and it is nowhere in
    // frame.
    //
    // This moves viewState rather than calling map.jumpTo after mount. The map is a *controlled*
    // component here — an imperative camera move on mount is undone by the very next render,
    // which re-applies viewState. Verified: the jumpTo version left the camera on the old course.
    //
    // Frames COURSE_FRAME_RADIUS_METRES, same as flyToCourse, so picking a course means the same
    // thing however it was picked. This used to recenter only and keep the visitor's zoom, which
    // left the one selection path that could still drop you onto a pin on a continent: pick a club
    // in the list at world zoom, switch to the map, and nothing had moved you in.
    if (
      next === 'map' &&
      selectedCourse?.longitude != null &&
      selectedCourse?.latitude != null
    ) {
      const camera = cameraForCourseFrame(
        selectedCourse.latitude,
        selectedCourse.longitude,
      );
      setViewState((current) => ({ ...current, ...camera }));
    }
    // The list scrolls the page, so a switch made deep in it would otherwise land the next view
    // part-way down. The map is exactly one screen and must start at the top.
    window.scrollTo({ top: 0 });
    setView(next);
  };

  const handleMapClick = (event: mapboxgl.MapLayerMouseEvent): void => {
    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }

    const clusterFeatures = map.queryRenderedFeatures(event.point, {
      layers: ['clusters'],
    });
    if (clusterFeatures.length > 0) {
      const clusterId = clusterFeatures[0].properties?.cluster_id;
      const source = map.getSource('courses') as mapboxgl.GeoJSONSource;
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          return;
        }
        const geometry = clusterFeatures[0].geometry;
        if (geometry?.type === 'Point') {
          // Clusters are the one place a click *should* zoom — that is what a cluster is asking
          // to be done to it. Step in rather than jumping the whole way to the split zoom: never
          // further in than the split (past it is wasted travel, the cluster is already gone) and
          // never more than clusterZoomStep allows from here. Clicking the same cluster again
          // walks it down the rest of the way.
          const currentZoom = map.getZoom();
          const expansionZoom = zoom ?? currentZoom;
          map.flyTo({
            center: geometry.coordinates as [number, number],
            zoom: Math.min(
              expansionZoom,
              currentZoom + clusterZoomStep(currentZoom),
            ),
          });
        }
      });
      return;
    }

    const pointFeatures = map.queryRenderedFeatures(event.point, {
      layers: ['unclustered-point'],
    });
    if (pointFeatures.length > 0) {
      // Mapbox hands back a serialized copy of the feature's properties rather than the original
      // object. Flat primitives survive that round trip intact, which is all a CourseMinifiedDTO
      // holds, so this cast is safe.
      const course = pointFeatures[0].properties as CourseMinifiedDTO;
      if (isStripWidth()) {
        handlePinOnStrip(course);
        return;
      }
      handleCourseFocus(course);
    }
  };

  // On a phone a pin tap brings that course's card up in the nearby strip rather than opening the
  // detail drawer, which covers the whole map there. The card's View button is how the visitor
  // opens the course. Already in the strip: select it, and the strip slides to its card (see
  // NearbyCoursesStrip), keeping the order the visitor was browsing. Not in it: make it the origin,
  // which re-sorts the strip around it and puts its card first.
  const handlePinOnStrip = (course: CourseMinifiedDTO): void => {
    const inStrip = nearbyCourses
      .slice(0, STRIP_COURSE_COUNT)
      .some((c) => isSameCourse(c.id, course.id));
    if (!inStrip) {
      setOriginCourse(course);
    }
    handleCourseSelect(course);
  };

  const selectedCourseDistance =
    selectedCourse &&
    userLocation &&
    selectedCourse.latitude != null &&
    selectedCourse.longitude != null
      ? calculateDistanceMeters(
          userLocation.latitude,
          userLocation.longitude,
          selectedCourse.latitude,
          selectedCourse.longitude,
        )
      : undefined;

  // Reference point for "nearest courses" flips: by default it's the visitor's own location,
  // but once a course is picked — from the search bar or by clicking its pin — the strip
  // re-centers on that course, so it reads as "nearest to this course" rather than "nearest to
  // me". Clicking a strip card does NOT move this — it only opens that course's details —
  // otherwise the strip would reshuffle out from under the visitor every time they tap a card.
  const nearbyOrigin =
    originCourse &&
    originCourse.latitude != null &&
    originCourse.longitude != null
      ? {
          latitude: originCourse.latitude,
          longitude: originCourse.longitude,
          label: originCourse.name ?? 'this course',
          key: originCourse.id ?? 'course',
        }
      : userLocation
        ? { ...userLocation, label: 'you', key: 'user' }
        : null;

  // One sort feeds both views: the strip takes the first STRIP_COURSE_COUNT, the list view pages
  // through the whole thing. Sorting twice for one origin would be pure waste.
  const nearbyCourses = useMemo(() => {
    if (!nearbyOrigin) {
      return [];
    }

    const ranked = visibleCourses
      .filter(
        (c) =>
          c.latitude != null &&
          c.longitude != null &&
          // Case-insensitive, per CLAUDE.md and matching nextNearbyCourse below. A case-sensitive
          // compare here would leave the origin in `ranked` as well as prepended at index 0, so
          // the drawer's "next nearby" would offer the open club as its own next.
          (!originCourse || !isSameCourse(c.id, originCourse.id)),
      )
      .map((c) => ({
        ...c,
        distanceMeters: calculateDistanceMeters(
          nearbyOrigin.latitude,
          nearbyOrigin.longitude,
          c.latitude as number,
          c.longitude as number,
        ),
      }))
      .sort((a, b) => a.distanceMeters - b.distanceMeters);

    // The course the list is built around leads it, rather than being filtered out of its own
    // results. It used to be excluded — it is zero metres from itself, so it carried no "nearby"
    // information — but that made searching for a course and then not finding it in the list read
    // as a bug. Both the strip and the full-page list read this array, so both get it.
    if (
      originCourse &&
      originCourse.latitude != null &&
      originCourse.longitude != null
    ) {
      return [
        { ...originCourse, distanceMeters: 0 },
        ...ranked.slice(0, MAX_NEARBY_COURSES - 1),
      ];
    }

    return ranked.slice(0, MAX_NEARBY_COURSES);
    // Depending on the primitive fields rather than the `nearbyOrigin` / `originCourse` objects is
    // deliberate. Both are rebuilt on every render, so listing the objects would re-sort all 41k
    // courses each time and defeat the memo entirely. The rule can't see that these fields are the
    // only parts actually read above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibleCourses,
    nearbyOrigin?.latitude,
    nearbyOrigin?.longitude,
    originCourse?.id,
  ]);

  // With a filter on, the List view is A to Z instead of nearest first: a filter is a list the
  // member asked for (their played courses, one country), and a name is how they look for a club
  // in it. No cap here, unlike the nearby order: A to Z cut at 500 stops at "C" in a big country.
  // The rows still page 48 at a time, so the length costs nothing on screen.
  // Every filtered course is in it, with or without coordinates, and with or without a location:
  // sorting by name needs neither. The distance is from the visitor only, never from a picked
  // course, because the heading no longer names what it is measured from.
  const isListAToZ = activeFilterCount > 0;
  const listCourses = useMemo(() => {
    if (!isListAToZ) {
      return nearbyCourses;
    }

    return visibleCourses
      .map((c) => ({
        ...c,
        distanceMeters:
          userLocation && c.latitude != null && c.longitude != null
            ? calculateDistanceMeters(
                userLocation.latitude,
                userLocation.longitude,
                c.latitude,
                c.longitude,
              )
            : undefined,
      }))
      .sort((a, b) => NAME_COLLATOR.compare(a.name ?? '', b.name ?? ''));
    // Primitive location fields for the same reason as nearbyCourses above: userLocation is not
    // the thing that changes, its coordinates are.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isListAToZ,
    nearbyCourses,
    visibleCourses,
    userLocation?.latitude,
    userLocation?.longitude,
  ]);

  // What the list view resets its scroll on. A to Z has no origin, so a change of filter is what
  // makes it a different list.
  const listKey = isListAToZ
    ? `a-z:${Array.from(courseFilters).sort().join(',')}:${geoFilter.country ?? ''}:${geoFilter.region ?? ''}`
    : (nearbyOrigin?.key ?? 'none');

  // One point — the open course — feeding the gold halo layer. Its own source rather than a
  // filter on the `courses` one: that source is clustered, so at anything but close zoom the open
  // course is folded into a cluster feature and there is no point left to match on.
  // The open course is drawn as its own bouncing marker, so it has to come out of the shared pin
  // layer — see buildUnclusteredPointLayer. Memoised on the id alone: this rebuilds a layer
  // definition, and handing <Layer> a fresh object on every map move would re-set the filter on
  // the 41k-feature source at frame rate.
  const unclusteredPointLayer = useMemo(
    () => buildUnclusteredPointLayer(selectedCourse?.id),
    [selectedCourse?.id],
  );

  // The course names flip with the ground the visitor picked, so this rebuilds only when the map
  // type changes, not on every move. Same reason as the layer above.
  const unclusteredPointLabelLayer = useMemo(
    () => buildUnclusteredPointLabelLayer(mapStyle.isLightGround),
    [mapStyle.isLightGround],
  );

  // Matched to that layer's `icon-size` so a club's pin does not change size at the moment it is
  // picked. viewState.zoom is already re-rendering this component on every move.
  const selectedPinScale = coursePinScaleForZoom(viewState.zoom);

  // The next course *in the nearby ordering*, for the drawer's hop row. This is the same array
  // the strip scrolls through and the list view pages, so "next" means the same thing on all
  // three surfaces — one step further out from the origin, not the closest neighbour of whatever
  // is open. Computing it as nearest-to-selected instead made the row bounce between two mutually
  // closest clubs forever.
  //
  // Its click runs handleCourseSelect, deliberately not handleCourseFocus: re-origining would
  // re-sort the array around the new course and turn every click back into that same bounce.
  // Leaving the origin alone is what lets repeated clicks walk down the list.
  const nextNearbyCourse = useMemo(() => {
    if (!selectedCourse) {
      return null;
    }

    const index = nearbyCourses.findIndex((c) =>
      isSameCourse(c.id, selectedCourse.id),
    );

    // -1 means the open course is not in the nearby set at all, so there is no "next" to offer.
    // Only reachable if the origin is further than MAX_NEARBY_COURSES clubs away from it.
    return index < 0 ? null : (nearbyCourses[index + 1] ?? null);
  }, [nearbyCourses, selectedCourse]);

  return (
    <div
      // Two hooks for index.css, both about keeping the Mapbox controls reachable.
      // explore-drawer-open slides them out from under the detail drawer; explore-strip-open
      // lifts them above the nearby strip on a phone, which otherwise buries the corner they sit
      // in. Both are map-view only: the list view has no controls to move.
      className={`explore-page${
        view === 'map' && isDetailOpen ? ' explore-drawer-open' : ''
      }${view === 'map' && nearbyOrigin ? ' explore-strip-open' : ''}`}
      // The map is a 100dvh app view. The list view is not: there the page itself
      // scrolls, because iOS only takes a tap on the status bar back to the top when the document
      // is what scrolls, and an inner scroll box left that tap doing nothing. That is also why the
      // top bar and every drawer are position: fixed rather than absolute. On the map, where this
      // box is exactly the viewport, the two are the same; on a long list, absolute would have
      // stretched them down the whole page.
      //
      // The drawers park off-screen with translate-x-full rather than unmounting. Being fixed,
      // they no longer add to this box's overflow, but the clip below stays as the guard for
      // anything absolute that does.
      //
      // `clip`, not `hidden`. Hidden only hides the overflow: the box is still a scroll container
      // at scrollWidth 750 on a 375px screen, and anything that asks to be scrolled into view
      // gets it. Opening the filter menu did exactly that and slid the whole top bar 83px off the
      // left of the screen with the parked drawer showing behind it. `clip` is not a scroll
      // container at all, so there is nothing to scroll and the same overflow is still hidden.
      style={
        {
          height: view === 'map' ? '100dvh' : undefined,
          minHeight: view === 'map' ? undefined : '100dvh',
          width: '100%',
          position: 'relative',
          overflow: 'clip',
          // Read by index.css to park the locate button above the nearby strip. A custom property
          // rather than an inline offset on the control, because that control is Mapbox's own DOM
          // and the rule that positions it already lives in the stylesheet.
          '--gp-strip-height': `${stripHeight}px`,
        } as React.CSSProperties
      }
    >
      {/* One blurred bar across the top: the played-count flag at the left and the GolfPlayed
          logo in the middle, at every width (the client's call, 2026-11-04). Everything else
          floats under it: the search box (with the Map / List switch inside it) and then the
          filter row. It is full-bleed rather than a floating pill so it reads as page chrome.
          A fixed h-14 rather than padding round the content, because the list view's top
          padding and the floating rows below are all measured from this bar's bottom edge. */}
      <div className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-charcoal/60 backdrop-blur-md">
        <div className="relative flex h-14 items-center px-3 sm:px-4">
          {/* Signing in, or getting to the profile, matters on a phone too, so the flag is never
              dropped. It is the only control in the bar. */}
          <ExploreAccountButton
            sessionVersion={sessionVersion}
            playedCount={playedCourseIds.size}
            onSignInClick={() => {
              setMemberSetupIntent(null);
              setIsMemberSetupOpen(true);
            }}
          />

          {/* Centred on the bar, not on the space left by the flag, so it sits in the true middle
              of the screen. pointer-events-none: it is a picture, and on a narrow phone it must
              never take a tap meant for the flag. It is a ~5:1 wordmark, so h-7 is ~142px wide,
              which clears the flag on a 320px screen. */}
          <img
            src="/images/logo-white.png"
            alt="GolfPlayed"
            className="pointer-events-none absolute left-1/2 top-1/2 h-7 w-36 -translate-x-1/2 -translate-y-1/2 object-contain"
          />

          {/* Out of the bar at every width, hung off its bottom edge and floating on the map. One
              layout, no breakpoint: every number below (the list view's top padding, the results
              dropdown) is measured against this one place.
              Centred with a translate rather than mx-auto, because it is absolutely positioned
              and there is no row left to centre it in. It inherits the bar's z-50, so it floats
              above the map. */}
          <div className="absolute left-1/2 top-full mt-2 w-[calc(100%-1.5rem)] max-w-xl -translate-x-1/2">
            <ExploreSearchBar
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              results={searchResults}
              onResultClick={handleSearchSelect}
              isLoading={isLoading}
              totalCourses={visibleCourses.length}
              isPlayed={isCoursePlayed}
              onTogglePlayed={handleTogglePlayed}
              // The debounced term, not the one being typed: the results are computed from it,
              // so this is what decides whether an empty list means "found nothing".
              isSearching={
                debouncedSearchTerm.trim().length >= SEARCH_MIN_CHARS
              }
              filterScope={filterScope}
              onRequestCourse={handleRequestCourse}
              // Map / List lives inside the search bubble, at its right-hand end.
              trailing={
                <ExploreViewToggle
                  view={view}
                  onChange={handleViewChange}
                  embedded
                />
              }
            />

            {/* Filters at the far left, then the member-list chips. After the search box in the
                DOM, with no z-index of its own, so the results dropdown paints over it. The
                drawer itself is a child of the page further down, because it runs the full
                height. */}
            <div className="mt-2">
              <ExploreFilterChips
                activeCount={activeFilterCount}
                isFilterOpen={isFilterOpen}
                onFilterClick={() => {
                  // One sheet at a time (R30). The filter runs the full height of the right-hand
                  // side and the map type sheet runs the width of the bottom, so open together
                  // they overlap in the corner.
                  setIsMapStyleOpen(false);
                  setIsFilterOpen((open) => !open);
                }}
                kinds={courseFilters}
                counts={filterCounts}
                isMember={isMember}
                onToggleKind={toggleCourseFilter}
                // Signed out, a list chip is the same gate as a tick (R2): the lists are a
                // member's own, so the chip asks them to sign in.
                onSignedOutChipClick={() =>
                  requireMember(
                    'your played courses and bucket list',
                    () => undefined,
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* One list view. It shows dense rows on a phone and the card grid on a wider screen;
          CourseListView picks the shape from the width. */}
      {view === 'list' ? (
        <CourseListView
          courses={listCourses}
          isAToZ={isListAToZ}
          selectedCourseId={selectedCourse?.id}
          onCourseClick={handleViewCourse}
          onBucketList={handleBucketList}
          onRequestToPlay={handleRequestToPlay}
          isPlayed={isCoursePlayed}
          isBucketListed={isCourseBucketListed}
          onTogglePlayed={handleTogglePlayed}
          originLabel={nearbyOrigin?.label ?? 'you'}
          originKey={listKey}
        />
      ) : (
        <>
          <Map
            {...viewState}
            ref={mapRef}
            onClick={handleMapClick}
            onLoad={handleMapLoad}
            onMove={(evt) => setViewState(evt.viewState)}
            mapboxAccessToken={MAP_BOX_KEY || ''}
            style={{ width: '100%', height: '100%' }}
            // The visitor's pick, defaulting to the brand Monochrome style. The list, the URLs
            // and the note about the account-scoped token all live in config/exploreMapStyles.ts.
            //
            // Swapping this calls map.setStyle, which throws away every source, layer and image
            // the old style held. Nothing here has to undo that by hand: react-map-gl re-adds the
            // <Source>/<Layer> children itself, and the course pin PNG comes back through the
            // `styleimagemissing` hook wired up in handleMapLoad — which is exactly the "later
            // style reload" that hook was put there for.
            mapStyle={mapStyleUrl(mapStyle)}
            // Globe instead of the default Mercator plane. At world zoom the whole course set
            // reads as a planet's worth of golf, and it drops Mercator's extreme distortion at
            // high latitudes — Scandinavian and Canadian courses sat visibly wrong before.
            // The style declares globe itself, so this is redundant today; it stays so the projection
            // can't silently revert to Mercator if someone edits the style in Studio.
            projection={{ name: 'globe' }}
            // There is deliberately no fog prop. Monochrome defines its own — black colour/high-color/
            // space-color with star-intensity 0 — which is the flat, no-atmosphere look we want. Setting
            // fog here overrides that and brings the halo and starfield back.
            //
            // Keeps the globe framed at world zoom. Rotation is intentionally left enabled: a globe you
            // can't spin doesn't read as a globe, so the old dragRotate={false} and the bearing/pitch
            // clamp in onMove are both gone.
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            touchZoomRotate
            touchPitch
            // Off so the explicit AttributionControl below can place it; without this the map
            // renders a second, default one in the bottom-right corner.
            attributionControl={false}
          >
            {/* Attribution moved off the bottom-right corner and over to the logo, so that
                corner holds nothing but the controls. That is what lets the CSS in index.css
                move the whole corner to the top on phones — see below — without dragging the
                attribution up there with it. Mapbox requires attribution to stay visible, not to
                stay in any particular corner. */}
            <AttributionControl position="bottom-left" compact />

            {/* One of two controls on the map (the map type button sits above it), and the one that
                does something the gestures cannot. Zoom went with the compass: a phone pinches, a desktop scrolls or
                double-clicks, and a cluster tap already flies the camera in, so the buttons were
                a third way to do what every visitor was doing another way. Finding yourself again
                on a world map has no gesture, so this one stays. No FullscreenControl either:
                the page is already a full-viewport map.

                Bottom-right at every width. The top is spoken for, with the search box across it
                and the detail drawer down the right-hand side. On a phone the nearby strip owns
                the bottom, so index.css lifts this corner above it while that strip is up
                (explore-strip-open) rather than moving the control somewhere else entirely. */}
            <GeolocateControl
              ref={geolocateRef}
              position="bottom-right"
              trackUserLocation
              showUserHeading
              onGeolocate={handleGeolocate}
              positionOptions={{ enableHighAccuracy: true }}
              // The control moves the camera itself before onGeolocate fires. Its cap matches
              // LOCATE_MAX_ZOOM so that first move lands at the same regional height our padded
              // fit then settles on — otherwise the visitor watches the map dive and climb back
              // out. Left at the library default (15) it does exactly that.
              fitBoundsOptions={{
                maxZoom: LOCATE_MAX_ZOOM,
                speed: 0.8,
                curve: 1.6,
              }}
            />

            {/* Stacked just above the locate button. After it on purpose: see MapStyleControl. */}
            <MapStyleControl
              isOpen={isMapStyleOpen}
              onClick={() => {
                // One sheet at a time (R30).
                setIsFilterOpen(false);
                setIsMapStyleOpen((open) => !open);
              }}
            />

            <Source
              id="courses"
              type="geojson"
              data={geoJsonData}
              cluster
              clusterMaxZoom={14}
              clusterRadius={50}
            >
              <Layer {...clusterLayer} />
              <Layer {...clusterCountLayer} />
              <Layer {...unclusteredPointLayer} />
              <Layer {...unclusteredPointLabelLayer} />
            </Source>

            {/* The open course's pin, hopping in place. This replaced a gold ring drawn around it.
                It is a DOM marker rather than a second symbol layer so the hop can be a CSS
                animation on the compositor: animating a paint property instead would repaint the
                whole 41k-feature map every frame for as long as a course is open, which on a phone
                is a real battery cost for a decoration. */}
            {selectedCourse?.latitude != null &&
              selectedCourse?.longitude != null && (
                <Marker
                  longitude={selectedCourse.longitude}
                  latitude={selectedCourse.latitude}
                  // The tip is the location, matching the symbol layer's `icon-anchor: bottom`.
                  anchor="bottom"
                >
                  <img
                    src={CourseImageConstants.COURSE_PIN}
                    alt=""
                    className="explore-selected-pin"
                    style={{
                      width: COURSE_PIN_WIDTH * selectedPinScale,
                      height: COURSE_PIN_HEIGHT * selectedPinScale,
                    }}
                  />
                </Marker>
              )}
          </Map>

          {nearbyOrigin && (
            <NearbyCoursesStrip
              courses={nearbyCourses.slice(0, STRIP_COURSE_COUNT)}
              selectedCourseId={selectedCourse?.id}
              onCourseClick={handleCourseSelect}
              onViewCourse={handleViewCourse}
              isCoursePlayed={isCoursePlayed}
              onTogglePlayed={handleTogglePlayed}
              originLabel={nearbyOrigin.label}
              originKey={nearbyOrigin.key}
              onHeightChange={handleStripHeightChange}
            />
          )}
        </>
      )}

      {/* Always rendered, not gated on selectedCourse: the drawer keeps painting the last
          course while it slides back out, and unmounting it would kill that animation.
          Outside the map/list branch, because a list row opens it too. Inside the map branch
          it was never mounted in the List view, so tapping a row did nothing visible. */}
      <CourseDetailDrawer
        // Null while the drawer is shut, which is what the drawer reads as "closed" — it keeps
        // painting the last course from its own ref on the way out. Closing therefore leaves
        // selectedCourse alone, so the club stays marked on the map with the bouncing pin and
        // the nearby ordering does not reshuffle underneath the strip.
        course={isDetailOpen ? selectedCourse : null}
        distanceMeters={selectedCourseDistance}
        nextCourse={nextNearbyCourse}
        isPlayed={isCourseIn(playedCourseIds, selectedCourse?.id)}
        isBucketListed={isCourseIn(bucketListCourseIds, selectedCourse?.id)}
        onClose={() => setIsDetailOpen(false)}
        onSelectCourse={handleViewCourse}
        onBucketList={handleBucketList}
        onTogglePlayed={handleTogglePlayed}
      />

      {/* Outside the map/list branch, because the filter narrows both views and has to survive a
          switch between them. Above the top bar's z-50 so it covers the button that opened it,
          and below the member gate's z-[60], which outranks everything while it is up. */}
      <CourseFilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        kinds={courseFilters}
        onToggleKind={toggleCourseFilter}
        counts={filterCounts}
        showKinds={showKindFilters}
        geo={geoFilter}
        onGeoChange={setGeoFilter}
        countries={filterCountries}
        regions={filterRegions}
        onClearAll={clearAllFilters}
        resultCount={visibleCourses.length}
      />

      {/* A child of the page rather than of the top bar, so it can run the full width along the
          bottom. It stays mounted through a view switch: the button that opens it is map-only,
          so there is nothing to reopen it from the list, and parking it is what animates it. */}
      <MapStyleDrawer
        isOpen={isMapStyleOpen}
        onClose={() => setIsMapStyleOpen(false)}
        value={mapStyleId}
        onChange={handleMapStyleChange}
      />

      {/* Outside the map/list branch on purpose: the gate can be tripped from the detail drawer,
          the list card or the nearby strip, so it must outlive a view switch. Last child and above
          the top bar's z-50, because while it is up it is the only thing being asked of the
          visitor. */}
      <MemberSetupDrawer
        isOpen={isMemberSetupOpen}
        initialMode={memberSetupMode}
        intent={memberSetupIntent}
        // Back to sign in on close, so the next trip through the gate is the normal D7 one and
        // not still the sign-up tab the marketing link asked for.
        onClose={() => {
          setIsMemberSetupOpen(false);
          setMemberSetupMode('signIn');
        }}
        // Nothing is replayed here on purpose (D10): the drawer closes itself and the visitor
        // ticks again. This clears the intent so a later trip does not reuse stale copy, and pulls
        // in whatever the member had already saved so their existing ticks show straight away.
        onAuthenticated={() => {
          setMemberSetupIntent(null);
          setSessionVersion((v) => v + 1);
          refreshMyCourses();
        }}
      />

      <CourseRequestDrawer
        isOpen={isCourseRequestOpen}
        onClose={() => setIsCourseRequestOpen(false)}
        initialCourseName={courseRequestName}
      />

      {/* The end of the free tier. Sits beside the sign-in gate rather than inside it: they are
          two different refusals (you are not signed in / you are signed in and out of room), and
          only one of them can be open at a time in practice, because the tick that trips this one
          only ever reaches the server for a signed-in member. */}
      <UpgradeDrawer
        isOpen={freeLimitMessage != null}
        message={freeLimitMessage}
        onClose={() => setFreeLimitMessage(null)}
      />
    </div>
  );
};

export default Explore;
